import React, { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, message as antMessage } from "antd";
import { KeyOutlined } from "@ant-design/icons";

import { postData } from "../../api/apiService";
import { AUTH } from "../../api/endpointMap";

/**
 * Self-service "change my password" modal for the LOGGED-IN user.
 *
 * Calls POST /auth/change-password ({ old_password, new_password }) which
 * verifies the current password server-side. This is the flow the admin
 * user-detail page's disabled "Set Password" tooltip refers to for one's
 * own account - the direct set-password endpoint deliberately rejects
 * self-targets so a credential change always proves knowledge of the
 * current password.
 *
 * Shared component: mounted from the admin header dropdown today; the
 * tenant navbar can reuse it as-is (no role assumptions inside).
 *
 * Mirrors SetPasswordModal's (AdminUserDetail) hygiene: form scrubbed on
 * every close/submit so plaintext never lingers in React state, backdrop
 * click disabled, min-8/max-128 rules.
 */
const ChangePasswordModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setServerError(null);
    setSubmitting(false);
  }, [open, form]);

  const handleClose = () => {
    form.resetFields();
    setServerError(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await postData(AUTH.CHANGE_PASSWORD, {
        old_password: values.old_password,
        new_password: values.new_password,
      });
      form.resetFields();
      antMessage.success("Password changed successfully.");
      onClose?.();
    } catch (err) {
      // postData surfaces the BE message as a string (e.g. "Current
      // password is incorrect"); fall back to generic wording.
      setServerError(
        typeof err === "string"
          ? err
          : err?.detail || err?.message || "Failed to change password.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Change password"
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Change Password"
      okButtonProps={{ icon: <KeyOutlined />, loading: submitting }}
      destroyOnClose
      width={480}
      // No backdrop-click / Esc close - a fat-finger must not discard a
      // half-typed password. Explicit Cancel only.
      maskClosable={false}
    >
      {serverError && (
        <Alert
          type="error"
          showIcon
          message={serverError}
          className="mb-3"
          closable
          onClose={() => setServerError(null)}
        />
      )}
      <Form form={form} layout="vertical" requiredMark onFinish={handleSubmit}>
        <Form.Item
          name="old_password"
          label="Current password"
          rules={[{ required: true, message: "Current password is required" }]}
        >
          <Input.Password
            placeholder="Your current password"
            autoComplete="current-password"
            autoFocus
          />
        </Form.Item>
        <Form.Item
          name="new_password"
          label="New password"
          dependencies={["old_password"]}
          rules={[
            { required: true, message: "New password is required" },
            { min: 8, message: "Min 8 characters" },
            { max: 128, message: "Max 128 characters" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value && value === getFieldValue("old_password")) {
                  return Promise.reject(
                    new Error("New password must differ from the current one"),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Type a new password (8-128 characters)"
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          label="Confirm new password"
          dependencies={["new_password"]}
          rules={[
            { required: true, message: "Please confirm the new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || value === getFieldValue("new_password")) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Re-type the new password"
            autoComplete="new-password"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
